/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { signIn } from "next-auth/react";
import toast from "react-hot-toast";
import { AuthShell } from "./auth-shell";

type Step = "form" | "otp" | "pending";

interface InvitePreview {
  email: string;
  role: string;
  roleLabel: string;
  shopName: string;
  status: string;
}

export default function SignUpPage({ isSeaaii = false }: { isSeaaii?: boolean }) {
  const router = useRouter();
  const hideSocial = isSeaaii;

  const inviteToken =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("invite")
      : null;

  // The "เริ่มทดลองฟรี 14 วัน" CTAs on the landing append ?trial=1 so we
  // know to provision a FREE-plan subscription with a 7-day grace
  // window on signup. Plain /auth/signup (e.g. from the footer
  // "สมัครใช้งาน" link) does NOT activate a trial — those are users
  // signing up for direct paid use or after redirect from an invite.
  const startTrial =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("trial") === "1"
      : false;

  // ถ้าเซ็ทผ่าน ?invite= ให้บังคับ redirect กลับไปหน้า invite — กันเคสคนเข้า
  // เว็บ signup ใหม่แล้วพลาดการ accept (DashboardContext จะบังคับ create-shop)
  const callbackUrl = inviteToken
    ? `/invite/${inviteToken}`
    : (typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("callbackUrl") || "/dashboard"
        : "/dashboard");

  const [step, setStep] = useState<Step>("form");
  const [loading, setLoading] = useState(false);
  const [refno, setRefno] = useState("");
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Seaaii self-signups are leads: the register endpoint returns
  // pendingApproval=true, the account is created under the Seaaii partner
  // with PENDING, and we show a "we'll call you" screen instead of logging
  // in (login is blocked server-side until the partner approves).
  const [pendingApproval, setPendingApproval] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  // Pre-fill อีเมลจาก invite (ล็อคไม่ให้แก้) เพื่อให้ accept ได้แน่นอน
  useEffect(() => {
    if (!inviteToken) return;
    let canceled = false;
    fetch(`/api/invite/${inviteToken}`)
      .then(async (res) => {
        const data = await res.json();
        if (canceled) return;
        if (!res.ok) {
          setInviteError(data.error === "INVITE_NOT_FOUND" ? "ไม่พบคำเชิญนี้" : "คำเชิญใช้ไม่ได้");
          return;
        }
        if (data.status !== "PENDING") {
          setInviteError(
            data.status === "ACCEPTED" ? "คำเชิญนี้ถูกใช้ไปแล้ว" :
            data.status === "REVOKED" ? "คำเชิญนี้ถูกยกเลิก" :
            "คำเชิญนี้หมดอายุ"
          );
          return;
        }
        setInvite(data);
        setFormData((prev) => ({ ...prev, email: data.email }));
      })
      .catch(() => !canceled && setInviteError("โหลดข้อมูลคำเชิญไม่สำเร็จ"));
    return () => { canceled = true };
  }, [inviteToken]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (otpExpiry <= 0) return;
    const t = setTimeout(() => setOtpExpiry((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpExpiry]);

  const formatExpiry = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      toast.error("กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }
    if (!/^0[0-9]{9}$/.test(formData.phone)) {
      toast.error("กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง (10 หลัก)");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }

    setLoading(true);
    try {
      const registerRes = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          // Pass the invite token through — the register endpoint uses
          // it to skip the IP rate limit for invited users (a teammate
          // on shared office/mobile NAT shouldn't be blocked because
          // someone else on the same IP signed up earlier).
          inviteToken: inviteToken || undefined,
          // ?trial=1 in the URL = customer came from the
          // "เริ่มทดลองฟรี 14 วัน" CTA. Register endpoint reads this
          // to create a FREE-plan Subscription with a 7-day endDate.
          startTrial: startTrial && !inviteToken ? true : undefined,
        }),
      });
      const registerData = await registerRes.json();
      if (!registerRes.ok) throw new Error(registerData.error || "เกิดข้อผิดพลาด");

      setPendingApproval(registerData.pendingApproval === true);

      const otpRes = await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: formData.phone,
          inviteToken: inviteToken || undefined,
        }),
      });
      const otpData = await otpRes.json();
      if (!otpRes.ok) throw new Error(otpData.error || "ไม่สามารถส่ง OTP ได้");

      setRefno(otpData.refno);
      setStep("otp");
      setResendCooldown(60);
      setOtpExpiry(30 * 60);
      toast.success(`ส่ง OTP ไปยัง ${formData.phone} แล้ว (Ref: ${otpData.refno})`);
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pinStr = pin.join("");
    if (pinStr.length < 6) {
      toast.error("กรุณากรอก OTP ให้ครบ 6 หลัก");
      return;
    }

    setLoading(true);
    try {
      const verifyRes = await fetch("/api/auth/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: formData.phone, pin: pinStr }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || "รหัส OTP ไม่ถูกต้อง");

      toast.success("ยืนยันเบอร์โทรศัพท์สำเร็จ!");

      // Seaaii leads are PENDING — login is blocked until the partner
      // approves, so skip auto-login and show the thank-you screen.
      if (pendingApproval) {
        setStep("pending");
        return;
      }

      const result = await signIn("credentials", {
        email: formData.email,
        password: formData.password,
        redirect: false,
      });
      if (result?.ok) router.push(callbackUrl);
    } catch (err: any) {
      toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/otp/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: formData.phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "ไม่สามารถส่ง OTP ได้");
      setRefno(data.refno);
      setPin(["", "", "", "", "", ""]);
      setResendCooldown(60);
      setOtpExpiry(30 * 60);
      toast.success(`ส่ง OTP ใหม่แล้ว (Ref: ${data.refno})`);
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    const next = [...pin];
    next[index] = value;
    setPin(next);
    if (value && index < 5) pinRefs.current[index + 1]?.focus();
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setPin(pasted.split(""));
      pinRefs.current[5]?.focus();
    }
  };

  const inputStyle: React.CSSProperties = {
    border: "1px solid var(--line)",
    background: "#fff",
    color: "var(--ink)",
  };

  const primaryBtnStyle: React.CSSProperties = {
    background: "var(--blue-700)",
    color: "#fff",
    boxShadow: "0 1px 0 rgba(255,255,255,.2) inset, 0 8px 22px -8px rgba(37,71,196,.55)",
  };

  return (
    <AuthShell
      isSeaaii={isSeaaii}
      panelTitle={<>เริ่มใช้ AI<br />ปิดการขายให้คุณ</>}
      panelSubtitle="ทดลองฟรี 14 วัน ไม่ต้องผูกบัตรเครดิต ติดตั้งใน 3 นาที — รวม Facebook + LINE ในกล่องเดียว"
    >
      {invite && step === "form" && (
        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-3.5 text-sm">
          <div className="font-semibold text-blue-900">🎉 คุณได้รับคำเชิญเข้าทีม</div>
          <div className="text-blue-800 mt-0.5">
            ร้าน <b>{invite.shopName}</b> · บทบาท <b>{invite.roleLabel}</b>
          </div>
          <div className="text-xs text-blue-700 mt-1.5">
            กรุณาสมัครด้วยอีเมลที่ได้รับคำเชิญเพื่อเข้าทีมโดยอัตโนมัติ
          </div>
        </div>
      )}

      {inviteError && step === "form" && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900">
          ⚠️ {inviteError} — สามารถสมัครเป็นบัญชีปกติได้ตามด้านล่าง
        </div>
      )}

      {step !== "pending" && (
      <div className="text-center mb-6">
        {step === "form" ? (
          <>
            <h1 className="text-[26px] font-bold tracking-tight" style={{ color: "var(--ink)" }}>
              สร้างบัญชีใหม่
            </h1>
            <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>
              เริ่มใช้งาน {isSeaaii ? "Seaaii" : "FlowCloser"} ฟรี 7 วัน
            </p>
          </>
        ) : (
          <>
            <h1 className="text-[24px] font-bold tracking-tight" style={{ color: "var(--ink)" }}>
              ยืนยันเบอร์โทรศัพท์
            </h1>
            <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>
              ส่ง OTP ไปยัง <span className="font-semibold" style={{ color: "var(--ink)" }}>{formData.phone}</span>
              {refno && (
                <span className="ml-1 text-xs" style={{ color: "var(--ink-3)", fontFamily: "'JetBrains Mono', monospace" }}>
                  (Ref: {refno})
                </span>
              )}
            </p>
          </>
        )}
      </div>
      )}

      {step === "pending" && (
        <div className="text-center py-4">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-3xl">
            🙏
          </div>
          <h1 className="text-[24px] font-bold tracking-tight" style={{ color: "var(--ink)" }}>
            ขอบคุณที่ให้ความสนใจ
          </h1>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>
            เราได้รับข้อมูลการสมัครของคุณเรียบร้อยแล้ว<br />
            ทีมงานจะติดต่อกลับไปที่เบอร์{" "}
            <span className="font-semibold" style={{ color: "var(--ink)" }}>{formData.phone}</span>{" "}
            ภายใน 24 ชั่วโมง
          </p>
          <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800">
            บัญชีของคุณอยู่ระหว่างรอการอนุมัติ จะสามารถเข้าสู่ระบบได้หลังทีมงานยืนยัน
          </div>
          <Link
            href="/"
            className="mt-6 inline-block text-sm font-semibold hover:underline"
            style={{ color: "var(--blue-700)" }}
          >
            ← กลับหน้าแรก
          </Link>
        </div>
      )}

      {step === "form" && (
        <form onSubmit={handleFormSubmit} className="space-y-3.5">
          <Field label="ชื่อ-นามสกุล">
            <input
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="ชื่อและนามสกุลของคุณ"
              className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
              style={inputStyle}
            />
          </Field>
          <Field label="อีเมล" hint={invite ? "ล็อคไว้ตามอีเมลที่ได้รับคำเชิญ" : undefined}>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={!!invite}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15 read-only:bg-[var(--bg-soft)] read-only:cursor-not-allowed"
              style={inputStyle}
            />
          </Field>
          <Field label="เบอร์โทรศัพท์" hint="จะส่ง OTP ยืนยันไปยังเบอร์นี้">
            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              maxLength={10}
              placeholder="0812345678"
              className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
              style={inputStyle}
            />
          </Field>
          <Field label="รหัสผ่าน">
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              placeholder="อย่างน้อย 6 ตัวอักษร"
              className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
              style={inputStyle}
            />
          </Field>
          <Field label="ยืนยันรหัสผ่าน">
            <input
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="ใส่รหัสผ่านอีกครั้ง"
              className="w-full px-4 py-3 rounded-xl text-[14px] outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
              style={inputStyle}
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 mt-2 rounded-xl text-[14px] font-semibold transition-all disabled:opacity-60"
            style={primaryBtnStyle}
          >
            {loading ? "กำลังสมัครสมาชิก..." : "สมัครสมาชิก"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={handleOtpSubmit} className="space-y-6">
          <div>
            <label className="block text-[13px] font-medium mb-3 text-center" style={{ color: "var(--ink-2)" }}>
              กรอกรหัส OTP 6 หลัก
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePinPaste}>
              {pin.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { pinRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(i, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(i, e)}
                  className="w-11 h-14 text-center text-xl font-bold rounded-xl outline-none transition-colors focus:border-[var(--blue-700)] focus:ring-2 focus:ring-[var(--blue-700)]/15"
                  style={{
                    border: "1px solid var(--line)",
                    background: "#fff",
                    color: "var(--ink)",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
              ))}
            </div>
            <div className="text-center mt-3">
              {otpExpiry > 0 ? (
                <p
                  className="text-sm font-semibold"
                  style={{ color: otpExpiry <= 60 ? "#dc2626" : "var(--blue-700)" }}
                >
                  OTP หมดอายุใน {formatExpiry(otpExpiry)}
                </p>
              ) : (
                <p className="text-sm font-semibold" style={{ color: "#dc2626" }}>
                  OTP หมดอายุแล้ว กรุณาขอใหม่
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || pin.join("").length < 6 || otpExpiry === 0}
            className="w-full py-3 rounded-xl text-[14px] font-semibold transition-all disabled:opacity-60"
            style={primaryBtnStyle}
          >
            {loading ? "กำลังยืนยัน..." : "ยืนยัน OTP"}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => { setStep("form"); setPin(["", "", "", "", "", ""]); }}
              className="hover:underline"
              style={{ color: "var(--ink-3)" }}
            >
              ← แก้ไขข้อมูล
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || loading}
              className="font-semibold hover:underline disabled:opacity-50"
              style={{ color: "var(--blue-700)" }}
            >
              {resendCooldown > 0 ? `ส่งใหม่ใน ${resendCooldown}s` : "ส่ง OTP ใหม่"}
            </button>
          </div>
        </form>
      )}

      {step === "form" && (
        <>
          {!hideSocial && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: "var(--line)" }} />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white" style={{ color: "var(--ink-3)" }}>
                    หรือ
                  </span>
                </div>
              </div>

              <button
                onClick={() => signIn("google", { callbackUrl })}
                className="w-full flex items-center justify-center gap-3 py-3 px-5 rounded-xl text-[14px] font-semibold transition-all bg-white hover:bg-[var(--bg-soft)]"
                style={{ border: "1px solid var(--line)", color: "var(--ink)" }}
              >
                <Image src="/icons/google.svg" alt="" width={18} height={18} />
                สมัครด้วย Google
              </button>
            </>
          )}

          <p className="text-center mt-6 text-sm" style={{ color: "var(--ink-2)" }}>
            มีบัญชีอยู่แล้ว?{" "}
            <Link
              href={`/auth/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`}
              className="font-semibold hover:underline"
              style={{ color: "var(--blue-700)" }}
            >
              {invite ? "เข้าสู่ระบบเพื่อยอมรับคำเชิญ" : "เข้าสู่ระบบ"}
            </Link>
          </p>

          <p className="text-xs text-center mt-4 leading-relaxed" style={{ color: "var(--ink-3)" }}>
            การสมัครสมาชิกหมายความว่าคุณยอมรับ{" "}
            <Link href="/terms" target="_blank" className="hover:underline" style={{ color: "var(--blue-700)" }}>
              เงื่อนไขการใช้งาน
            </Link>
            {" และ "}
            <Link href="/terms" target="_blank" className="hover:underline" style={{ color: "var(--blue-700)" }}>
              นโยบายความเป็นส่วนตัว
            </Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1.5" style={{ color: "var(--ink-2)" }}>
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-xs mt-1" style={{ color: "var(--ink-3)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}
